import React, { useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "../i18n/I18nProvider";
import { QuotePaymentFormState, QuotePaymentMethod } from "../types/leadWizard";

interface QuotePaymentStepProps {
  initialValues: QuotePaymentFormState;
  onSubmit: (values: QuotePaymentFormState) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
  error?: string | null;
}

const sanitizeCardNumber = (value: string) => value.replace(/\D/g, "");

const formatCardNumber = (value: string) => {
  const digits = sanitizeCardNumber(value).slice(0, 19);
  if (digits.startsWith("34") || digits.startsWith("37")) {
    return digits
      .replace(/(\d{4})(\d{6})(\d{0,5})/, (_, p1, p2, p3) =>
        [p1, p2, p3].filter(Boolean).join(" ")
      )
      .trim();
  }
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

const luhnCheck = (digits: string) => {
  if (!digits || digits.length < 12) {
    return false;
  }
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = parseInt(digits.charAt(i), 10);
    if (Number.isNaN(digit)) {
      return false;
    }
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

const detectCardBrand = (digits: string): string | null => {
  if (!digits) return null;
  if (/^4/.test(digits)) return "Visa";
  if (/^5[1-5]/.test(digits)) return "Mastercard";
  if (/^2(2[2-9]|[3-6][0-9]|7[0-1]|720)/.test(digits)) return "Mastercard";
  if (/^3[47]/.test(digits)) return "American Express";
  if (/^6(?:011|5)/.test(digits)) return "Discover";
  return null;
};

const isExpirationInPast = (month: number, year: number) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (year < currentYear) {
    return true;
  }
  if (year === currentYear && month < currentMonth) {
    return true;
  }
  return false;
};

export const QuotePaymentStep: React.FC<QuotePaymentStepProps> = ({
  initialValues,
  onSubmit,
  onBack,
  isSubmitting,
  error,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] =
    useState<QuotePaymentFormState>(initialValues);
  const [localError, setLocalError] = useState<string | null>(null);
  const [lastActiveMethod, setLastActiveMethod] = useState<QuotePaymentMethod>(
    initialValues.method === "NONE" ? "CARD" : initialValues.method
  );

  const handleChange = (
    field: keyof QuotePaymentFormState,
    value: string | boolean
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMethodChange = (method: QuotePaymentMethod) => {
    if (formValues.providePaymentLater) {
      return;
    }
    setLastActiveMethod(method);
    setFormValues((prev) => ({
      ...prev,
      method,
    }));
  };

  const handleProvideLaterChange = (checked: boolean) => {
    if (checked) {
      setFormValues((prev) => ({
        ...prev,
        providePaymentLater: true,
        method: "NONE",
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      providePaymentLater: false,
      method: prev.method === "NONE" ? lastActiveMethod : prev.method,
    }));
  };

  const handleCardNumberChange = (rawValue: string) => {
    const formatted = formatCardNumber(rawValue);
    const digits = sanitizeCardNumber(formatted);
    const brand = detectCardBrand(digits);
    setFormValues((prev) => ({
      ...prev,
      cardNumber: formatted,
      cardCompany: brand ?? "",
      cardType: brand ? "CREDIT_CARD" : prev.cardType,
    }));
  };

  const validate = (): boolean => {
    if (!formValues.confirmAccuracy) {
      setLocalError(t("quote.payment.errors.confirmAccuracy"));
      return false;
    }

    if (formValues.providePaymentLater) {
      const hasBillingAddress =
        formValues.billingAddress1.trim() &&
        formValues.billingCity.trim() &&
        formValues.billingStateCode.trim() &&
        formValues.billingZipCode.trim();
      if (!hasBillingAddress) {
        setLocalError(t("quote.payment.errors.billingAddress"));
        return false;
      }
    }

    if (formValues.method === "CARD" && !formValues.providePaymentLater) {
      const digits = sanitizeCardNumber(formValues.cardNumber);
      if (!digits) {
        setLocalError(t("quote.payment.errors.cardNumber"));
        return false;
      }
      if (digits.length < 13 || digits.length > 19 || !luhnCheck(digits)) {
        setLocalError(t("quote.payment.errors.cardNumberInvalid"));
        return false;
      }
      const monthNumber = Number(formValues.expirationMonth);
      const yearNumber = Number(formValues.expirationYear);
      if (
        !formValues.expirationMonth.trim() ||
        !formValues.expirationYear.trim() ||
        Number.isNaN(monthNumber) ||
        Number.isNaN(yearNumber) ||
        monthNumber < 1 ||
        monthNumber > 12
      ) {
        setLocalError(t("quote.payment.errors.expirationInvalid"));
        return false;
      }
      if (isExpirationInPast(monthNumber, yearNumber)) {
        setLocalError(t("quote.payment.errors.expirationPast"));
        return false;
      }
      if (!formValues.cvv.trim()) {
        setLocalError(t("quote.payment.errors.cvv"));
        return false;
      }
      if (!/^\d{3,4}$/.test(formValues.cvv.trim())) {
        setLocalError(t("quote.payment.errors.cvvInvalid"));
        return false;
      }
    } else if (formValues.method === "BANK" && !formValues.providePaymentLater) {
      if (!formValues.bankName.trim()) {
        setLocalError(t("quote.payment.errors.bankName"));
        return false;
      }
      if (!formValues.accountNumber.trim()) {
        setLocalError(t("quote.payment.errors.accountNumber"));
        return false;
      }
      if (!formValues.abaRouting.trim()) {
        setLocalError(t("quote.payment.errors.abaRouting"));
        return false;
      }
    }

    setLocalError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(formValues);
    } catch (err: any) {
      setLocalError(err?.message ?? t("wizard.errors.generic"));
    }
  };

  const renderCardFields = () => (
    <>
      <Form.Group controlId="paymentCardNumber" className="mb-3">
        <Form.Label>{t("quote.payment.cardNumber")}</Form.Label>
        <Form.Control
          value={formValues.cardNumber}
          onChange={(event) => handleCardNumberChange(event.target.value)}
          inputMode="numeric"
          maxLength={23}
          placeholder="4111 1111 1111 1111"
          disabled={formValues.providePaymentLater}
        />
      </Form.Group>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="paymentCardType">
            <Form.Label>{t("quote.payment.cardType")}</Form.Label>
            <Form.Select
              value={formValues.cardType}
              onChange={(event) => handleChange("cardType", event.target.value)}
              disabled={formValues.providePaymentLater}
            >
              <option value="CREDIT_CARD">{t("quote.payment.cardTypeCredit")}</option>
              <option value="DEBIT_CARD">{t("quote.payment.cardTypeDebit")}</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="paymentCardCompany">
            <Form.Label>{t("quote.payment.cardCompany")}</Form.Label>
            <Form.Control
              value={formValues.cardCompany}
              onChange={(event) => handleChange("cardCompany", event.target.value)}
              placeholder="Visa, MasterCard..."
              disabled={formValues.providePaymentLater}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="paymentExpirationMonth">
            <Form.Label>{t("quote.payment.expirationMonth")}</Form.Label>
            <Form.Control
              value={formValues.expirationMonth}
              onChange={(event) => handleChange("expirationMonth", event.target.value)}
              placeholder="MM"
              inputMode="numeric"
              maxLength={2}
              disabled={formValues.providePaymentLater}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group controlId="paymentExpirationYear">
            <Form.Label>{t("quote.payment.expirationYear")}</Form.Label>
            <Form.Control
              value={formValues.expirationYear}
              onChange={(event) => handleChange("expirationYear", event.target.value)}
              placeholder="YYYY"
              inputMode="numeric"
              maxLength={4}
              disabled={formValues.providePaymentLater}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="paymentCvv">
            <Form.Label>{t("quote.payment.cvv")}</Form.Label>
            <Form.Control
              value={formValues.cvv}
              onChange={(event) => handleChange("cvv", event.target.value)}
              placeholder="123"
              type="password"
              inputMode="numeric"
              maxLength={4}
              disabled={formValues.providePaymentLater}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const renderBankFields = () => (
    <>
      <Form.Group controlId="paymentBankName" className="mb-3">
        <Form.Label>{t("quote.payment.bankName")}</Form.Label>
        <Form.Control
          value={formValues.bankName}
          onChange={(event) => handleChange("bankName", event.target.value)}
          disabled={formValues.providePaymentLater}
        />
      </Form.Group>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="paymentAccountNumber">
            <Form.Label>{t("quote.payment.accountNumber")}</Form.Label>
            <Form.Control
              value={formValues.accountNumber}
              onChange={(event) => handleChange("accountNumber", event.target.value)}
              disabled={formValues.providePaymentLater}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="paymentAba">
            <Form.Label>{t("quote.payment.abaRouting")}</Form.Label>
            <Form.Control
              value={formValues.abaRouting}
              onChange={(event) => handleChange("abaRouting", event.target.value)}
              disabled={formValues.providePaymentLater}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const renderBillingFields = () => (
    <>
      <Form.Group controlId="paymentHolder" className="mb-3">
        <Form.Label>{t("quote.payment.paymentHolder")}</Form.Label>
        <Form.Control
          value={formValues.paymentHolder}
          onChange={(event) => handleChange("paymentHolder", event.target.value)}
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group controlId="billingAddress1" className="mb-3">
        <Form.Label>{t("quote.payment.billingAddress1")}</Form.Label>
        <Form.Control
          value={formValues.billingAddress1}
          onChange={(event) => handleChange("billingAddress1", event.target.value)}
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group controlId="billingAddress2" className="mb-3">
        <Form.Label>{t("quote.payment.billingAddress2")}</Form.Label>
        <Form.Control
          value={formValues.billingAddress2}
          onChange={(event) => handleChange("billingAddress2", event.target.value)}
          disabled={isSubmitting}
        />
      </Form.Group>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="billingCity">
            <Form.Label>{t("quote.payment.billingCity")}</Form.Label>
            <Form.Control
              value={formValues.billingCity}
              onChange={(event) => handleChange("billingCity", event.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="billingStateCode">
            <Form.Label>{t("quote.payment.billingStateCode")}</Form.Label>
            <Form.Control
              value={formValues.billingStateCode}
              onChange={(event) =>
                handleChange("billingStateCode", event.target.value.toUpperCase())
              }
              disabled={isSubmitting}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="billingZipCode">
            <Form.Label>{t("quote.payment.billingZipCode")}</Form.Label>
            <Form.Control
              value={formValues.billingZipCode}
              onChange={(event) => handleChange("billingZipCode", event.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  return (
    <div className="card">
      <div className="card-header">
        <button
          type="button"
          className="button button-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("buttons.back")}
        </button>
      </div>
      <div className="card-body">
        <h3 className="card-title">{t("quote.payment.title")}</h3>
        <p className="section-helper">{t("quote.payment.helper")}</p>

        {(localError || error) && (
          <Alert variant="danger">{localError || error}</Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="paymentMethod" className="mb-3">
            <Form.Label>{t("quote.payment.methodLabel")}</Form.Label>
            <Form.Select
              value={formValues.method}
              onChange={(event) =>
                handleMethodChange(event.target.value as QuotePaymentMethod)
              }
              disabled={formValues.providePaymentLater}
            >
              <option value="CARD">{t("quote.payment.methodCard")}</option>
              <option value="BANK">{t("quote.payment.methodBank")}</option>
            </Form.Select>
          </Form.Group>

          <Form.Check
            type="checkbox"
            id="providePaymentLater"
            className="mb-3"
            checked={formValues.providePaymentLater}
            onChange={(event) => handleProvideLaterChange(event.target.checked)}
            label={t("quote.payment.provideLater")}
            disabled={isSubmitting}
          />

          {formValues.method === "CARD"
            ? renderCardFields()
            : formValues.method === "BANK"
            ? renderBankFields()
            : null}

          {renderBillingFields()}

          <Form.Check
            type="checkbox"
            id="confirmAccuracy"
            className="mb-3"
            checked={formValues.confirmAccuracy}
            onChange={(event) => handleChange("confirmAccuracy", event.target.checked)}
            label={t("quote.payment.confirmAccuracy")}
          />

          <div className="form-actions">
            <Button
              type="submit"
              className="button button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("quote.payment.submitting")}
                </>
              ) : (
                t("quote.payment.submit")
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
